import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTemplate } from '../../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../../utils/Button';
import FocusControl from '../../../../../utils/FocusControl';
import InputField, {
    InputDateField,
    SelectOption,
    TextareaField,
    addOption
} from '../../../../../utils/InputField';
import cn from '../../../../../utils/cn';
import { handlePaymentReceive } from '../../InvoiceOperation';
import { useInvoiceInfo } from '../InvoiceInfoProvider';

const paymentOptions = [
    {
        id: 'cash',
        title: 'Cash'
    },
    {
        id: 'card',
        title: 'Card'
    },
    {
        id: 'bank-transfer',
        title: 'Bank Transfer'
    },
    {
        id: 'wire-transfer',
        title: 'Wire Transfer'
    },
    {
        id: 'cheque',
        title: 'Cheque'
    }
];

function PaymentReceiveModal({ closeModal, invoiceData }) {
    const { id, amountDue } = invoiceData;
    const [isError, setIsError] = useState('');
    const [loading, setLoading] = useState(false);
    const { setMessage } = useTemplate();
    const { handleReload } = useInvoiceInfo();
    const [formData, setFormData] = useState({
        InvoiceId: id,
        Amount: amountDue || 0,
        ReceiveDate: dayjs().format('YYYY-MM-DD'),
        PaymentMethod: '',
        PartialAmount: 0,
        IsPartialPayment: false,
        Note: ''
    });

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleIsPartialPayment = (e) => {
        const { checked } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            IsPartialPayment: checked,
            PartialAmount: checked ? prevData.Amount : 0
        }));
    };

    const payload = useMemo(() => {
        return {
            ...formData,
            Amount: Number(formData.Amount),
            PartialAmount: Number(formData.PartialAmount)
        };
    }, [formData]);
    const handleSaveClick = (e) => {
        e?.preventDefault();
        setLoading(true);
        handlePaymentReceive(id, payload, setIsError)
            .then(({ title }) => {
                closeModal();
                handleReload();
                setMessage({ type: 'success', text: title });
            })
            .catch((err) => {
                setLoading(false);
                setMessage({ type: 'error', text: err.toString() });
            });
    };
    FocusControl(isError, setIsError, formData);
    return (
        <form onSubmit={handleSaveClick}>
            <div className="flex flex-col gap-y-3 mb-5">
                <InputField
                    label="Amount"
                    name="Amount"
                    placeholder="Type Amount..."
                    formData={formData}
                    onChange={handleChange}
                    readOnly
                    required
                />
                <div className={cn('flex flex-col w-full custom-field relative')}>
                    <div className="flex justify-between items-center pl-1 mb-1 text-sm md:text-base capitalize w-full font-normal text-left">
                        <span>
                            Partial Amount <span className="text-error"> *</span>
                        </span>
                        <label className="flex justify-between items-center gap-2">
                            <span>Partial Payment</span>
                            <input
                                type="checkbox"
                                name="IsPartialPayment"
                                onChange={handleIsPartialPayment}
                                className="mr-1"
                                checked={formData.IsPartialPayment}
                            />
                        </label>
                    </div>
                    <input
                        type="text"
                        name="PartialAmount"
                        value={formData.PartialAmount}
                        onChange={handleChange}
                        placeholder="Partial Amount"
                        readOnly={!formData.IsPartialPayment}
                        autoComplete="off"
                        className={cn(
                            `flex-shrink w-full h-8.5 md:h-10 bg-transparent rounded-lg block outline-none border border-primary-200 px-2 text-primary text-xs md:text-sm leading-6 font-normal read-only:border-primary-100 read-only:bg-primary-50/50 read-only:text-primary-700 read-only:cursor-default`
                        )}
                    />
                </div>
                <InputDateField
                    label="Receive Date"
                    title="Receive Date"
                    name="ReceiveDate"
                    formData={formData}
                    onChange={handleChange}
                    required
                />

                <SelectOption
                    label="Payment Method"
                    name="PaymentMethod"
                    placeholder="Payment Method"
                    formData={formData}
                    onChange={handleChange}
                    options={paymentOptions?.map((d) => addOption(d.id, d.title, d.id))}
                    required
                />

                <TextareaField
                    label="Transaction Note"
                    name="Note"
                    placeholder="Type Note..."
                    formData={formData}
                    onChange={handleChange}
                    optional
                    className="h-36"
                />
            </div>

            <div className="flex justify-end">
                <div className="flex items-center gap-x-3">
                    <ButtonCancel title="Cancel" onClick={closeModal} />
                    <ButtonPrimary title="Save" btnType="submit" loading={loading} />
                </div>
            </div>
        </form>
    );
}

export default PaymentReceiveModal;
